/**
 * content-strings.ts — W66.X17
 *
 * Markdown content inlined as TypeScript string constants.
 * Generated from /client/src/content/ markdown files.
 * No ?raw imports — avoids TypeScript ambient module requirement.
 *
 * Canon: no "Nine Gates", "Floors", "Navy", "Maybe", BANT, MEDDIC.
 * Bryan K. Short, Founder and CEO, Mi6 Group. W66.X17 2026-06-06.
 */

export const endOfCrmManifesto = `# The End of the CRM Era

**Mi6 Group · Attilius**

---

The CRM was built for a different problem.

It was built for volume. Process 10,000 leads, run them through funnels, qualify down. The platform's job was to hold the names and track the touches. Quantity was the metric. Volume was the strategy.

That model served its era. But enterprise solution selling was never a volume problem.

An AE working Mi6 methodology carries 9 to 12 accounts. Not 10,000 names. Not a pipeline that requires a CRM just to recall who was called last. The problem is not volume management — it is evidence management. What do you know, how sure are you, and what do you need to go get next.

The CRM has no answer to that question. It can store a note. It cannot tell you whether the note you wrote constitutes evidence that the budget is real. It can remind you to call. It cannot tell you what to say when you do.

That is the gap M-PACT was built to close.

---

## The Intelligence Problem

A rep walks out of a buyer meeting with a conversation in their head and a blank CRM field in front of them. The CRM asks: "what happened?" The rep types something. The note is filed. The manager reads it on Friday and tries to reconstruct whether the deal is moving.

Everyone in that chain is guessing. The rep is guessing what is worth recording. The manager is guessing what the note means. The forecast call is a negotiation between guesses.

Mi6 runs a different chain. The question is not "what happened?" The question is: what do you know, and how do you know it?

That is the Trichotomy. Not a binary — not yes or no. Three positions: I absolutely know. I think I know. I do not know yet.

A rep who can answer the Trichotomy for every checkpoint across every account in their book is not guessing. They are operating with evidence. The platform makes that evidence legible — to the rep, to the manager, and to the coaching layer that never sleeps.

---

## What M-PACT Replaces

BANT was a start. Budget, Authority, Need, Timeline — four questions you could ask to determine if a deal was worth pursuing. It was useful in 1960 when B2B sales was simpler.

But BANT treats qualification as a checklist. Answer yes to all four, the deal is qualified. Move on.

The problem is that "yes" decays. A budget that existed in Q1 may not exist in Q3. An authority figure who agreed in February may have been replaced in April. A need that was urgent in January may have been deprioritized when the CEO changed the strategy.

M-PACT is not a checklist. It is an ongoing diagnostic. Ten checkpoints, each with its own evidence threshold, each capable of decaying and triggering re-qualification. The system does not let a deal sit at "qualified" indefinitely. It watches.

---

## What Attilius Does

Attilius is the automated version of the methodology.

It does not make the effort for the rep. It makes the gaps visible.

When CP3 (Budget) has not been confirmed in 21 days, Attilius surfaces it. When CP6 (Technical and Financial Sign-off) shows no named signers, Attilius flags it. When a rep overrides a score-driven forecast assignment, Attilius logs the override, asks for a written defense, and surfaces it to the manager.

The rep operates. The platform makes the operation legible, repeatable, and coachable.

That is the end of the CRM era. Not the end of tracking — but the end of tracking as the primary value. The primary value is intelligence: knowing what you know, knowing what you are missing, and knowing what to do about it.

---

*Mi6 Group, powered by Attilius.*
*Bryan K. Short, Founder and CEO, Mi6 Group.*
`;

export const crmIntelligenceLayerManifesto = `# The Intelligence Layer

**Mi6 Group · Attilius**

---

A CRM records what happened. An intelligence platform tells you what to do next.

That is the distinction that matters. Not the number of fields. Not the dashboard count. Not the integrations catalog. The question is: after every interaction, every meeting, every email, does the system leave the rep smarter or busier?

---

## The CRM Tax

Every CRM in production today levies a tax on the rep's time. The tax is called data entry. Log the call. Update the stage. Add a note. Attach the email. Move the deal to the next stage.

The tax compounds. A rep managing 10 accounts with 3 active contacts each, running cadences of 4-6 touches per week, spends 60-90 minutes per day in data entry. That is 30-40% of a productive sales day, every day, just to keep the CRM current.

And the CRM does not spend that data back. The manager reviews it on Friday. The forecast call happens. Nobody learned anything the rep did not already know.

---

## The Intelligence Contract

Attilius works on a different contract. The contract is: you give the system your evidence, the system tells you what it means and what you are missing.

Not what happened — what it means.

The rep records that the VP of Sales confirmed budget in the meeting. Attilius reads that as CP3 (Budget) — Formal state, basis captured. It cross-checks CP6 (Technical and Financial Sign-off): is the VP of Sales also the financial signer, or is there a separate CFO in the approval chain? If the VP is not the signer, CP6 remains open and Attilius surfaces a homework item: name the financial signer.

The rep did not have to know to ask that question. The methodology knew. The platform asked it.

That is the intelligence contract. The rep brings the evidence. The platform brings the methodology.

---

## The Doctor Model

We are not in the business of evangelizing. We are in the business of problem-solving.

Doctors do not show up at someone's house asking if they have considered that they might be sick. The patient walks in because something hurts. The doctor's job is to diagnose accurately and treat correctly.

The same principle governs M-PACT qualification. A rep working Mi6 methodology does not chase accounts that have not surfaced a real problem. No pain, no deal. The rep does not nurture a painless account for six months hoping something will emerge. That is evangelism. Mi6 does not do evangelism.

CP2 — Need, Pain, Demonstration — is the doctor's exam. Get the diagnosis wrong here and the rest of the treatment plan collapses. The budget conversation is meaningless if the pain is not real. The timeline is irrelevant if the need has not been established.

The intelligence layer is the diagnostic instrument. It holds the rep to the standard of the diagnosis, not the standard of the activity log.

---

## From "I Think" to "I Know"

The core transformation M-PACT drives in a sales organization is the shift from assumption to evidence.

"I think I know" is not a crime. It is the starting point. A rep early in an account carries a hypothesis. The hypothesis is directionally correct but not yet confirmed. Attilius holds the hypothesis as Soft Yes — it contributes partial credit to the score, generates a homework item, and reminds the rep what confirmation looks like.

Over time, the rep accumulates evidence. The Soft Yes promotes to Firm Yes when the artifact is captured: the written confirmation, the named source, the document attached. The score improves. The forecast bucket changes. The manager can see not just what the rep is calling, but why the call is defensible.

That movement — from "I think I know" to "I absolutely know" — is the intelligence layer working. It is not a CRM filling fields. It is a methodology demanding evidence.

---

*Mi6 Group, powered by Attilius.*
*Bryan K. Short, Founder and CEO, Mi6 Group.*
`;

export const methodologyAsEngine = `# Methodology as Engine

**Mi6 AIP — Operating Doctrine**
*Bryan K. Short, Founder and CEO, Mi6 Group*

---

## The Mental Model

A sales professional walks out of Mi6 Bootcamp Training with three things in hand:

1. **An annual Sales Plan** — quota, quarterly targets, monthly extrapolation. Definitional.
2. **A methodology** — M-PACT, ten checkpoints CP1-CP10, the Helix Health model, the Tower visualization, the Forecast canon. Doctrine.
3. **A platform** — the operating environment where the Sales Plan and the methodology meet, where the work happens every day.

The platform's job is to make those three things one thing. Not three.

The rep does not browse a methodology page, then visit a forecast page, then check a cadence page. They operate inside a workspace where the Sales Plan is the spine, the methodology is the engine that decomposes the plan into work, and the platform is the surface where they live.

---

## M-PACT — What It Stands For

M-PACT is the integrated answer. Buy any one pillar in isolation and it regresses by Q+2.

| Letter | Word | One-line meaning |
|---|---|---|
| **M** | Methodology | The defined way of selling Mi6 Group has built and trained for nearly two decades. |
| **P** | Process / Prioritization | The process carries a deal from suspect to won. Prioritization is fundamental — knowing what to work on, in what order, and what to put down. |
| **A** | Accountability | Transparency across the team and inside each deal. Team members own their numbers, accounts, and commitments; the system makes that ownership visible. |
| **C** | Cadence / Coaching | A particular cadence of events has to be followed to succeed, and ongoing coaching has to happen along the way to maximize every checkpoint. |
| **T** | Timing / Timeline management | Timing is the hardest thing to measure and manage — being in the right place at the right time, not on the outside looking in, not wasting your time or theirs on a deal whose timing will never work. |

**Methodology is the master.** The other four are how it runs every week.

---

## Accelerated Jumpstart — The 90-Day Engagement Program

Every engagement begins with an Accelerated Jumpstart: a 90-day program tied to the engagement start date. Day 30 and Day 60 are milestones inside the 90-day window, not separate horizons.

| Milestone | Window | Purpose |
|---|---|---|
| **Jumpstart Day 30** | Days 1–30 | Coverage and diagnosis. CP1-CP4 complete on priority accounts. Top-10 identified and scored. Coaching cadence active. |
| **Jumpstart Day 60** | Days 31–60 | Advancement and gap closure. Weakest checkpoints addressed. Buying-committee gaps mapped. Forecast commitments validated against the Sales Plan quarterly target. |
| **Jumpstart Day 90** | Days 61–90 | Program close. Pipeline health reviewed. Accounts promoted or qualified out. Transition to full annual-plan operating cadence. |

The engagement start date drives the clock. If an engagement starts in August, the Accelerated Jumpstart covers August through October, still within the annual plan. After Day 90, the platform continues from the same Sales Plan, surfacing the balance of the year against quota.

---

## The Ten Checkpoints

CP1 through CP10 are the diagnostic engine. Not a funnel. Not a stage. A set of ten evidence thresholds that must be met before a deal can be called closed.

**Qualification half (CP1-CP4):** Is there a real deal here?

- **CP1 — Project:** Is there a real project, and is this a new logo or an existing customer expansion?
- **CP2 — Need / Pain / Demonstration:** Is there a recognized need we can address, measured by quantified pain in the buyer's own words, validated by demonstration of our solution in their context?
- **CP3 — Budget:** Is there real money to pay for this? Three valid answers: Formal (named, allocated, line item exists), Discretionary (must be carved from an existing budget), No (buyer has to sell internally to get budget approved).
- **CP4 — Timeline:** Is there a decision window AND an implementation window? Formal (named dates with internal commitment behind them) or ad hoc (a hope, a "soon," no committed dates).

**Execution and Closing half (CP5-CP10):** How do we close it?

- **CP5 — Authority (Project Initiative):** Who owns the initiative inside the customer org?
- **CP6 — Technical and Financial Sign-off:** Who actually signs? Two authorities — technical and financial — usually two different people.
- **CP7 — Org Structure and Influence Map:** What does the org chart say, and where does the real influence sit?
- **CP8 — Competitive Position:** Where are we against the alternatives?
- **CP9 — Compliance (IT Internal and Industry-Specific):** What are the IT internal and industry-specific compliance requirements?
- **CP10 — Legal (Contracts, SLAs, Terms):** Paper path. Contracts, service-level agreements, terms and conditions.

---

## The Methodology Page in the Platform

The Methodology Workspace threads all three horizons — Strategic, Tactical, Execution — together on one screen with coaching woven in throughout. It is not a documentation page. It is a working surface.

**H1 — CP Catalog:** The ten M-PACT checkpoints with weights, disqualifier status, doctrine notes, and key questions.

**H2 — Process Flow:** The M-PACT question-decision tree for each checkpoint. How answers branch, how evidence requirements activate.

**H3 — Tower View:** The architectural visualization of the pipeline. Each deal at its current checkpoint level.

**H4 — Helix View:** The DNA health diagnostic. Each checkpoint maps to a health orb. Orb color reflects answer strength.

---

## The Operator Test

Every surface in the platform passes the Operator Test before it ships:

> Could a professional who just walked out of Mi6 Bootcamp Training, with only their Sales Plan in hand, sit down at this surface and know what to do next within 30 seconds — without asking, without browsing, without consulting a manual?

If yes: ship.
If no: redesign.

---

*Mi6 Group, powered by Attilius.*
*Bryan K. Short, Founder and CEO, Mi6 Group.*
`;

export const attiliusWhyDifferent = `# Why Attilius Is Different

**Mi6 Group · Attilius**

---

There are hundreds of sales tools. Most of them do the same thing: they hold data and remind you to do things.

Attilius does something different. It tells you whether what you are doing is working.

---

## The Difference Between Activity and Evidence

A CRM tracks activity. Calls made. Emails sent. Meetings booked. The activity log is full. The deal does not move.

This is the most common failure mode in enterprise sales. High activity, low advancement. The rep is busy. The manager sees green on the dashboard. The quarter ends and the deal was never real.

Attilius tracks evidence, not activity. The distinction matters.

An email sent is an activity. An email where the buyer named the budget line item is evidence for CP3. A meeting booked is an activity. A meeting where the buyer introduced the financial signer is evidence for CP6. The same event — a conversation, an exchange, a meeting — is either activity or evidence depending on what was learned and captured.

Attilius knows the difference. M-PACT defines what evidence looks like for each checkpoint. Attilius asks whether the evidence has been gathered, holds the rep to the standard of gathering it, and surfaces the gap when it has not been met.

---

## The Trichotomy

Every evidence question in Attilius resolves to one of three states:

**I absolutely know.** The artifact exists. The source is named. The date is captured. This is Firm Yes or Firm No — unambiguous, documented, defensible.

**I think I know.** The hypothesis is strong. The customer agreed, or the signal is clear. But the artifact is not yet captured. This is Soft Yes — partial credit, homework generated.

**I do not know yet.** No signal. No hypothesis. The question has not been asked. This generates a homework item with a suggested question and a suggested source.

The Trichotomy means no rep in Attilius is ever in a situation where they feel they must pretend certainty they do not have. "I do not know" is not a failure state. It is an honest record of where the account stands. It generates work, not a penalty.

What the Trichotomy prevents is the most dangerous state in sales: false certainty. A rep who calls a deal "qualified" because the buyer said "sounds interesting" is not qualified — they are in a Soft Yes at best, and probably a homework item. Attilius does not let that state sit unexamined.

---

## Why "Not a CRM" Is Not a Marketing Claim

When Mi6 says Attilius is not a CRM, it is describing a structural difference in purpose.

A CRM is built for the manager. It exists so the manager can see what the rep is doing, so the VP can see what the manager is seeing, so the executive team can see what the VP is reporting. The data flows up the chain for oversight.

Attilius is built for the person doing the work. The intelligence flows to the person who needs it at the moment they can act on it. The rep sees the gap in CP7 (Org Structure and Influence Map) because they are about to walk into a meeting where that gap will determine whether the deal advances. They do not see it on Friday in a manager review — they see it Tuesday morning before the call.

That is the architectural difference. Attilius is an intelligence accumulator with a recommendation engine on top. The CRM is a record-keeping system with a reporting layer on top.

Same data. Different purpose. Different outcomes.

---

## The Coaching Layer

Mi6 has been coaching sales professionals for nearly two decades. The methodology did not start as software. It started as a curriculum — a way of teaching people to think about deals, to ask the right questions, to recognize when they were guessing and when they knew.

Attilius is the automated version of that coaching. The questions the platform asks are the questions an Mi6 coach would ask in a deal review. The homework items the platform generates are the actions an Mi6 coach would assign. The flags the platform raises — CP3 not confirmed in 21 days, no Champion mapped at an account, a score-driven forecast override without a written defense — are the concerns an Mi6 coach would voice.

The difference is that the coach is always present. Not just on Fridays. Not just for the deals that made it onto the call agenda. Every account, every checkpoint, every day.

---

## The Standard: "I Think I Know" to "I Absolutely Know"

The movement that matters in M-PACT is the movement from hypothesis to evidence.

Every deal starts with hypotheses. The rep thinks the budget is real. The rep thinks the buyer has authority. The rep thinks the competition is limited. These are not wrong starting points — they are the natural starting state when an account is new.

The methodology's job is to move each hypothesis toward confirmed evidence, one checkpoint at a time. Attilius tracks that movement, surfaces what is still hypothetical, and tells the rep what kind of evidence would convert the hypothesis to fact.

A deal where every checkpoint has moved from "I think I know" to "I absolutely know" is a deal that will close when the timing is right. A deal where the rep is still saying "I think" across CP3, CP6, and CP7 is a deal that will slip.

Attilius knows which one you have.

---

*Mi6 Group, powered by Attilius.*
*Bryan K. Short, Founder and CEO, Mi6 Group.*
`;

export const cp1Model = `# CP1 — Project

**Checkpoint weight:** 5 of 100
**Qualification half:** Yes (CP1-CP4 — Is there a real deal here?)

---

## What CP1 Tests

Is there a real project, and is this a new logo or an existing customer expansion?

CP1 is the entry gate. It establishes whether there is a genuine initiative at the account before any further qualification effort is invested. A "no" at CP1 stops all work immediately — no budget conversation, no needs analysis, no timeline discussion. The deal does not enter the pipeline.

The false-positive cost at CP1 is one wasted pass at CP2 and CP3. That is acceptable. The methodology is built to catch false positives early and cheaply.

---

## The Seven Questions

**Q1 — Is there a real project here?**
The foundational gate. A Firm No from the buyer is a terminal drop. The rep records the date and source and moves on. A Soft Yes or an "I think I know" generates a homework item with a suggested next step.

**Q2 — Is this customer in an industry we serve?**
Industry Checkpoint. A Firm No is a hard stop. If the account is outside Mi6's target industry list, the rep stops investing time. A Soft Yes generates a confirmation task.

**Q3 — Does what we sell solve the kind of problem this customer has?**
First pain check. Establishes that the category of need matches what Mi6 can address. A Firm Yes earns entry to CP2. This is not the deep pain diagnosis — that is CP2's job.

**Q3-ext — Have we done a project similar to this one before?**
Risk diagnostic, not a gate. Establishes whether Mi6 has relevant comparable experience. Feeds the repeatability argument in CP2.

**Q4 — Is this an existing customer or a new logo?**
Routing question. New logo engagements follow the full qualification path. Existing customer expansions may have pre-cleared some checkpoints from prior work. The platform routes accordingly.

**Q5 — Is this buying process formal or informal?**
Process diagnostic. Formal processes (RFx, procurement committee, defined timelines) require different positioning and different evidence gathering than informal ones. This question branches into sub-funnels that set expectations for CP3-CP6.

**Q6 — Have we identified who is leading this project on the customer's side?**
Project owner identification. The account cannot advance past CP1 without a named project owner. This person is the candidate for CP5 (Authority — Project Initiative) later.

**Q7 — Does the customer have money set aside for this project right now?**
Budget pre-screen. Not the full CP3 budget analysis — a quick signal on whether budget has been allocated or must be built. Connects directly to CP3's three-state gate (Formal / Discretionary / No budget yet).

---

## Scoring Logic

The Trichotomy governs every answer:
- **I absolutely know** — full credit, artifact capture triggered within 7 days or the answer auto-demotes
- **I think I know** — partial credit, homework item generated
- **I do not know yet** — neutral (never penalized), homework item with suggested question and source

Firm No on Q1 or Q2 is a terminal stop. The deal does not enter the system.

---

## Doctrine Note

CP1 is deliberately shallow. Its job is to prevent wasted effort, not to qualify the deal. A rep who can answer the seven questions with confidence has earned the right to enter CP2. The deep diagnostic work begins there.

*Source: CANON_VISUALS_LOCKED.md, Mi6 Master Canon 2026-05-29.*
`;

export const cp2Model = `# CP2 — Need / Pain / Demonstration

**Checkpoint weight:** 20 of 100 (largest single checkpoint)
**Qualification half:** Yes (CP1-CP4 — Is there a real deal here?)

---

## What CP2 Tests

Is there a recognized need we can address (Need), measured by quantified pain in the buyer's own words (Pain), validated by demonstration of our solution in their context (Demonstration)?

All three sub-elements are worked together. Fit is the implicit outcome — if all three clear, the deal is, by definition, a fit.

CP2 carries the largest single weight in the methodology for a reason. Get the diagnosis wrong here and the rest of the qualification collapses. A budget conversation is meaningless if the pain is not real. A timeline is irrelevant if the need has not been established.

---

## The Doctor Frame

Bryan K. Short's teaching frame for CP2: "We are acting like a doctor. We are deciding if we can treat the symptom at root cause, and if insurance will pay for it because they see it as significant enough that it needs to be done versus a nice-to-have."

The doctor does not arrive at a diagnosis by asking the patient "do you think you might be sick?" The doctor asks specific questions, observes specific evidence, and arrives at a diagnosis that can be defended. Mi6 does the same.

---

## The Nine Questions

**Q1 — What pain is the customer trying to solve — in their own words?**
Capture the exact language. Not a Mi6 hypothesis the customer agreed with — the customer's own framing of their own problem. A paraphrase that the customer validated is Soft Yes. Their own words, captured, is Firm Yes.

**Q2 — Can the customer put numbers on the pain?**
The measurement gate. Quantified pain in the buyer's own words is the evidence standard. "It costs us X" or "we lose Y per month because of this" is Firm Yes. "It's a real problem" is Soft Yes at best.

**Q3 — How severe is the pain for the customer right now?**
Severity diagnostic. This is the central fork in CP2. Low-severity pain does not justify the friction of buying and implementing an enterprise solution. The rep must establish that the pain is acute enough to motivate a decision.

**Q4 — Have we verified the pain independently — beyond the customer's own words?**
Independent validation. Can Mi6 verify the pain through industry benchmarks, prior work at comparable accounts, or technical review? This establishes that the rep is not relying solely on the buyer's self-report.

**Q5 — Does what we sell match the pain end-to-end?**
Fit gate. Four outcomes: fully (the solution addresses the pain completely), partially (the solution addresses the primary pain, secondary gaps noted), partnership-required (Mi6 alone is insufficient, a partner addresses the gap), not enough (the pain is out of scope). Partial and partnership-required stay in the pipeline with documented gaps.

**Q6 — Have we (or our company) delivered the same outcome for a comparable customer?**
Repeatability deeper than CP1 Q3-ext. Establishes that Mi6 can point to a reference of comparable scope and industry. De-risks the buyer's internal selling conversation.

**Q7 — Does the customer's leadership see this as a corporate problem worth funding?**
The insurance check. Leadership visibility is the threshold between a project the buyer wants and a project the company will fund. This is the universal floor at CP2 — the pain must be visible above the buyer's own organizational level.

**Q8 — Have we run a demonstration where both sides confirmed the treatment fits the diagnosis?**
Mutual confirmation. Not a demo in the generic sense — a demonstration specifically designed to show that Mi6's solution addresses the specific pain the buyer articulated. Both sides confirm the fit. This locks CP2.

**Q9 — Has the customer explicitly agreed to move into budget, timing, and authority?**
CP2 exit gate. The buyer must agree that the need and pain are established before the rep advances to CP3 (Budget), CP4 (Timeline), and CP5 (Authority). This is not a formality — it is evidence that the buyer is engaged.

---

## Scoring Logic

CP2 is the gravitational center of the M-PACT score. 20 out of 100 points. A weak CP2 cannot be compensated by strong scores elsewhere — a deal without confirmed pain is not a deal.

Firm No on Q1 (no pain the customer is actively trying to solve) is a terminal drop. The rep records the date and source and moves the account to a lower priority. No pain, no deal.

---

## The Out-of-Order Principle

Information surfaces when the conversation allows. A buyer might reveal budget detail during a CP2 pain conversation. A buyer might mention the financial signer while discussing the pain's organizational visibility. When that happens, capture it now. Do not wait until the methodology "reaches" that checkpoint.

The CP number is a shorthand for evidence completeness, not a stage in a funnel.

*Source: CANON_VISUALS_LOCKED.md, Mi6 Master Canon 2026-05-29.*
`;

export const cp3Model = `# CP3 — Budget

**Checkpoint weight:** 10 of 100
**Qualification half:** Yes (CP1-CP4 — Is there a real deal here?)

---

## What CP3 Tests

Is there real money to pay for this? Three valid answers: (1) Formal — named, allocated, line item exists; (2) Discretionary — must be carved from an existing budget the buyer controls; (3) No — buyer has to sell internally to get budget approved.

Each state triggers different downstream behavior. Only State 1 and State 2 advance the deal past CP3.

---

## CP3 vs CP6

This distinction must be internalized:

**CP3 confirms the money exists.**
**CP6 confirms the money will be released.**

These are separate checkpoints by design. The line-item budget owner is not necessarily the financial signer. Even if a director confirms $200K is in their fiscal-year budget (CP3 clears), the purchase still routes through finance at the appropriate level — CFO, controller, financial signing authority — at CP6.

A rep who clears CP3 and considers the financial question answered has not done CP6. Both must be worked.

---

## The ROI Principle

Bryan K. Short's locked test: "Don't build a $50 fence around a $5 cow."

The cost of the solution must be materially smaller than the cost of the pain — by a margin big enough to justify the friction of buying and implementing. CP3 confirms the math is in the buyer's favor. If the pain the buyer quantified in CP2 is smaller than the cost of the solution, the deal has a structural problem that pricing alone cannot solve.

---

## The Three Budget States

**State 1 — Formal Budget.**
Named, allocated, line item exists. Source named. Approval thresholds known. Signal is strongest. The money is set aside for a specific project — "use it or lose it" pressure works in the deal's favor. Default health color: Green.

**State 2 — Discretionary Budget.**
Buyer must carve from an existing budget they control. Real but fragile. The rep must identify which budget, who controls it, who must approve the carve. Fewer approval hurdles than Formal, but the money is not committed and can be redirected. Default health color: Yellow until carve source and approval path are named, then promotes to Green.

**State 3 — No Budget.**
Buyer must sell internally before anything else can happen. The deal does not advance past CP3 until the buyer returns with State 1 or State 2. Default health color: Red. CP3 stays open with a sub-task: "Buyer building internal case." The rep's job is to equip the buyer for that internal selling conversation, not to continue with external qualification.

---

## The Six Questions

**Q1 — What is the budget state for this project?**
The three-state gate. This is the CP3 clearance question. Formal, Discretionary, or No budget yet. The answer must come from the buyer, not from the rep's assumption.

**Q2 — What kind of spend is this — capital expense, operating expense, or other?**
Capex vs. opex matters for approval routing. Capital expenses often require different approval chains and may have annual budget cycles. Operating expenses may have more flexibility but more scrutiny on recurring cost.

**Q3 — How is the customer planning to pay for it?**
Cash, lease, financed, or other. Payment structure affects the timeline and the approval chain. A financed purchase has different CP10 paper requirements than a cash purchase.

**Q4 — Have we confirmed a real number?**
The amount or range. "There is budget" is not enough. The rep must confirm an approximate number, or at minimum a range, to assess whether the solution is in-scope for what the buyer controls.

**Q5 — Does the cost of the solution land well below the cost of the pain?**
The ROI gate. The math must be visible and documented. The buyer will be asked this question by their internal approvers. The rep must be able to answer it before the buyer is.

**Q6 — What is the approval path from here to released funds?**
Path captured. CP6 will deepen the signer side, but CP3 establishes the routing: who must approve, in what sequence, at what threshold. The rep needs this to set a credible timeline at CP4.

---

## Scoring Logic

Formal and Discretionary budget states score essentially equal at the headline level. The deal-quality difference is captured in the basis funnel (how do you know), not in the headline score. Only No-budget-case-build scores meaningfully different — it signals the deal is not yet at CP3.

*Source: CANON_VISUALS_LOCKED.md, Mi6 Master Canon 2026-05-29.*
`;

export const cp4Cp10Summaries = `# CP4 through CP10 — Summaries

*Full model files for CP4-CP10 are in development. Summaries below are sourced from the Mi6 Master Canon, locked 2026-05-29 by Bryan K. Short, Founder and CEO, Mi6 Group.*

---

## CP4 — Timeline

**Weight:** 10 of 100
**Qualification half:** Yes (CP1-CP4)

Is there a decision window AND an implementation window? The rep must name both.

Two valid states:
- **Formal timeline** — named dates with internal commitment behind them. A board decision date, a fiscal year end, a regulatory deadline. The rep can name the date and the commitment mechanism behind it.
- **Ad hoc timeline** — a hope, a "soon," no committed dates. The deal can still advance, but the rep must document that the timeline is not yet firm and work CP4 actively.

A deal with no timeline is a deal that cannot be forecast. CP4 open without a path to formal timeline is a flag for coaching.

---

## CP5 — Authority (Project Initiative)

**Weight:** 15 of 100
**Execution half:** Yes (CP5-CP10)

Who owns the initiative inside the customer org? The sponsor — the person whose name is on this project, who can begin work at an approval level. Not the same as a signer.

CP5 is often confused with CP6. CP5 is the person accountable for the project's existence. CP6 is the person who releases the money and signs the paper. In most mid-market and enterprise deals, these are different people.

A deal without a named CP5 sponsor cannot advance. The buying committee cannot be mapped without knowing who is driving the initiative internally.

---

## CP6 — Technical and Financial Sign-off

**Weight:** 15 of 100
**Execution half:** Yes (CP5-CP10)

Who actually signs? Two authorities, usually two different people:
- **Technical sign-off** — CTO, IT lead, security officer, engineering manager, or a delegated senior individual contributor.
- **Financial sign-off** — CFO, controller, financial signing authority.

In smaller organizations, CP5 and CP6 may collapse to the same person. In most mid-market and enterprise deals, they are separate and must be named separately.

Teaching example: a VP of Sales owns the initiative (CP5). The VP of Sales does not perform technical sign-off — a senior engineer on the data team does (CP6 technical). The AE must engage both, even though the engineer is junior to the sponsor.

---

## CP7 — Org Structure and Influence Map

**Weight:** 15 of 100
**Execution half:** Yes (CP5-CP10)

What does the org chart say, and where does the real influence sit?

Champions, supporters, influencers, detractors. Who is acting for professional reasons, personal reasons, or political reasons. The org chart shows one thing; the power map shows another. Only mapped by asking many questions and meeting many people.

CP7 is the hidden-risk checkpoint. Deals that look clean on CP1-CP6 fail at CP7 because an influencer the rep never met surfaced a concern in the final approval committee. The buying committee map is the instrument for surfacing this risk before it surfaces itself.

---

## CP8 — Competitive Position

**Weight:** 5 of 100
**Execution half:** Yes (CP5-CP10)

Where are we against the alternatives?

Two competition types:
1. **A real competitor** doing what Mi6 does — possibly better. The rep names the competitor, describes what they offer, and documents where Mi6 wins and where the competitor wins.
2. **The buyer's existing infrastructure** — spreadsheets, manual workarounds, an older system doing what Attilius would automate. This is the more common competition in enterprise. Status quo is always the default option.

Both are real competition. The rep names which one they are facing.

---

## CP9 — Compliance (IT Internal and Industry-Specific)

**Weight:** 3 of 100
**Execution half:** Yes (CP5-CP10)

What are the IT internal compliance requirements AND the industry-specific compliance requirements? Both must be met before legal can close.

CP9 is the technical compliance checkpoint, distinct from CP10 contract terms. IT security review, data residency requirements, integration standards, and industry-specific regulatory requirements (HIPAA, SOC2, ISO, etc.) all land here.

CP9 is late-stage but can be a hard stop. A deal that has cleared CP1-CP8 can stall at CP9 if an IT security review raises a requirement Mi6 cannot meet. The rep surfaces the compliance requirements early — at CP5 when the technical signer is named — so there are no surprises at CP9.

---

## CP10 — Legal (Contracts, SLAs, Terms)

**Weight:** 2 of 100
**Execution half:** Yes (CP5-CP10)

Contracts, service-level agreements, terms and conditions, paper path.

Nothing financial or technical at this point — those were cleared upstream at CP3 (Budget), CP6 (Financial Sign-off), and CP9 (Compliance). CP10 is paper. The legal checkpoint is the path from verbal agreement to executed contract.

CP10 being the last checkpoint does not mean it should be left until the end. The rep surfaces paper requirements at CP5 when the buyer's procurement process is mapped. Standard MSA, custom terms, procurement portal requirements, and SLA levels should all be identified before CP9 closes.

---

*Source: Mi6 Master Canon, locked 2026-05-29 by Bryan K. Short, Founder and CEO, Mi6 Group.*
`;
